import { lazy, Suspense } from "react";
import {
  createBrowserRouter, createRoutesFromElements, Link, Navigate, Outlet, Route, RouterProvider, useLocation,
} from "react-router-dom";
import { AppShell } from "@/components/layout/app-shell";
import { ThemeProvider } from "@/components/layout/theme";
import { EmptyState, Spinner } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import { ToastProvider } from "@/components/ui/toast";
import { AuthProvider, useAuth } from "@/lib/auth";
import { landingPath } from "@/lib/nav";
import { atLeast, type Role } from "@/lib/types";
import { AudioPage } from "@/routes/audio";
import { CalendarPage } from "@/routes/calendar";
import { CalendarsPage } from "@/routes/calendars";
import { CallsPage } from "@/routes/calls";
import { CampaignPage } from "@/routes/campaign";
import { CampaignsPage } from "@/routes/campaigns";
import { CadencePage } from "@/routes/cadence";
import { CadencesPage } from "@/routes/cadences";
import { ContactPage } from "@/routes/contact";
import { PersonPage } from "@/routes/person";
import { ConversationsPage } from "@/routes/conversations";
import { ContactsPage } from "@/routes/contacts";
import { CustomFieldsPage } from "@/routes/custom-fields";
import { FirmPage } from "@/routes/firm";
import { FirmsPage } from "@/routes/firms";
import { OpportunitiesPage } from "@/routes/opportunities";
import { DispositionsPage } from "@/routes/dispositions";
import { DomainsPage } from "@/routes/domains";
import { FloorPage } from "@/routes/floor";
import { EndpointsPage } from "@/routes/endpoints";
import { GatewaysPage } from "@/routes/gateways";
import { HistoryPage } from "@/routes/history";
import { InventoryPage } from "@/routes/inventory";
import { ForgotPasswordPage } from "@/routes/forgot-password";
import { InvitePage } from "@/routes/invite";
import { LoginPage } from "@/routes/login";
import { NumbersPage } from "@/routes/numbers";
import { PhonePage } from "@/routes/phone";
import { CampaignReportPage } from "@/routes/campaign-report";
import { LineReportPage } from "@/routes/line-report";
import { RepReportPage } from "@/routes/rep-report";
import { SettingsPage } from "@/routes/settings";
import { SettingsHubPage } from "@/routes/settings-hub";
import { ResetPasswordPage } from "@/routes/reset-password";
import { SignInAsPage } from "@/routes/sign-in-as";
import { SkillsPage } from "@/routes/skills";
import IntakeFlowPage from "@/routes/intake-flow";
import { StagesPage } from "@/routes/stages";
import { TenantsPage } from "@/routes/tenants";
import { TasksPage } from "@/routes/tasks";
import { EmailTemplatePage } from "@/routes/email-template";
import { TextTemplatePage } from "@/routes/text-template";
import { TextTemplatesPage } from "@/routes/text-templates";
import { TriggerPage } from "@/routes/trigger";
import { TriggersPage } from "@/routes/triggers";
import { MessagesPage } from "@/routes/messages";
import { TeamPage, TeamsPage } from "@/routes/teams";
import { UsersPage } from "@/routes/users";
import { VoicemailMessagesPage } from "@/routes/voicemail-messages";
import { WebhookPage } from "@/routes/webhook";
import { SlackPage } from "@/routes/slack";
import { SmsSettingsPage } from "@/routes/sms";
import { EmailSettingsPage } from "@/routes/email";
import { VoicesPage } from "@/routes/voices";
import { SpeedDialsPage } from "@/routes/speed-dials";
import { SystemUpdatesPage } from "@/routes/system-updates";
import { BlockedCallersPage } from "@/routes/blocked-callers";
import { WebhooksPage } from "@/routes/webhooks";
import { WorkflowPage } from "@/routes/workflow";
import { WorkflowsPage } from "@/routes/workflows";
import { AudioSetupHost } from "@/softphone/audio-setup";
import { SoftphoneProvider } from "@/softphone/softphone-context";
import { TranscriptionProvider } from "@/softphone/transcription-widget";

/**
 * The phone's state gallery, development only. `import.meta.env.DEV` is a compile-time constant,
 * so the production bundle carries neither the route nor the module behind it.
 */
const DevPhonePage = import.meta.env.DEV ? lazy(() => import("@/routes/dev-phone")) : null;

export function App() {
  return (
    <ThemeProvider>
      <RouterProvider router={router} />
    </ThemeProvider>
  );
}

/**
 * What every screen sits in: the providers, inside the router so that anything they draw can link
 * and navigate. A data router (`createBrowserRouter`) rather than `BrowserRouter`, because only a
 * data router can hold a navigation, the browser's Back and Forward included, until a page with
 * unsaved changes has asked (`useBlocker`, ACL-404).
 */
function Root() {
  return (
    <AuthProvider>
      <ToastProvider>
        <SoftphoneProvider>
          <TranscriptionProvider>
            <Booted />
            {/* Inside the phone's provider, because only a user who HAS a phone has a
                microphone question to answer. It opens itself once after sign-in and is
                reopened from the phone's options menu and the settings page. */}
            <AudioSetupHost />
          </TranscriptionProvider>
        </SoftphoneProvider>
      </ToastProvider>
    </AuthProvider>
  );
}

/** The screen the address names, once the boot refresh has settled. */
function Booted() {
  const { loading } = useAuth();

  // Hold the routes until the boot refresh settles. Rendering them first would flash the login
  // screen at every already-signed-in user on every reload.
  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-canvas">
        <Spinner className="size-5" />
      </div>
    );
  }
  return <Outlet />;
}

/** A screen that failed to draw. Without it a data router shows its own page for developers. */
function RouteError() {
  return (
    <EmptyState
      className="min-h-dvh"
      title="Something went wrong"
      description="This page could not be shown. Reloading usually fixes it."
      action={
        <Button variant="outline" onClick={() => window.location.reload()}>
          Reload
        </Button>
      }
    />
  );
}

/** `/reports` (a link from before the three reports were three screens) opens the line report,
 *  with whatever filter it carried. */
function ReportsRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: "/reports/lines", search: location.search }} replace />;
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<Root />} errorElement={<RouteError />}>
      <Route path="/login" element={<LoginPage />} />
      {/* No session needed: the invitation's link carries its own credential (ADR-028). */}
      <Route path="/invite" element={<InvitePage />} />
      {/* No session needed either: an admin's one-time sign-in link is its own credential (ADR-040). */}
      <Route path="/sign-in-as" element={<SignInAsPage />} />
      {/* A rep who cannot sign in asks here, and the link their email carries opens the second
          one. Both are outside every guard for the obvious reason (ADR-057). */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {DevPhonePage && (
        <Route
          path="/dev/phone"
          element={
            <Suspense fallback={null}>
              <DevPhonePage />
            </Suspense>
          }
        />
      )}
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<Landing />} />
        <Route path="phone" element={<PhonePage />} />
        {/* No `RequireRole`: it is every signed-in user's own calls, and core-api narrows the
            rows to the caller's own endpoint whoever asks. */}
        <Route path="history" element={<HistoryPage />} />
        {/* Every signed-in person, as Contacts is. `:inbox` is team, mine, following or assigned;
            anything else lands on the Team Inbox. */}
        <Route path="conversations" element={<Navigate to="/conversations/team" replace />} />
        <Route path="conversations/:inbox" element={<ConversationsPage />} />
        {/* Every signed-in person: the chats they are in (platform ADR-186). */}
        <Route path="messages" element={<MessagesPage />} />
        <Route path="messages/:id" element={<MessagesPage />} />
        {/* Settings is the page of cards behind the foot of the sidebar; the signed-in person's own
            account (`SettingsPage`, from before there was one) is its last card. */}
        <Route path="settings" element={<SettingsHubPage />} />
        <Route path="settings/account" element={<SettingsPage />} />
        {/* Every signed-in person: the console's changelog (ACL-631), at the foot of the sidebar. */}
        <Route path="system-updates" element={<SystemUpdatesPage />} />
        <Route path="contacts" element={<ContactsPage />} />
        <Route path="contacts/:leadId" element={<ContactPage />} />
        <Route path="people/:personId" element={<PersonPage />} />
        <Route path="opportunities" element={<OpportunitiesPage />} />
        {/* Every signed-in person: core-api lists the caller's own tasks (ACL-408). */}
        <Route path="tasks" element={<TasksPage />} />
        <Route
          path="calendars"
          element={<CalendarsPage />}
        />
        <Route
          path="calendars/settings/:calendarId"
          element={
            <RequireRole minimum="supervisor">
              <CalendarPage />
            </RequireRole>
          }
        />
        <Route
          path="firms"
          element={
            <RequireRole minimum="supervisor">
              <FirmsPage />
            </RequireRole>
          }
        />
        <Route
          path="firms/:firmId"
          element={
            <RequireRole minimum="supervisor">
              <FirmPage />
            </RequireRole>
          }
        />
        <Route
          path="stages"
          element={
            <RequireRole minimum="supervisor">
              <StagesPage />
            </RequireRole>
          }
        />
        <Route
          path="custom-fields"
          element={
            <RequireRole minimum="admin">
              <CustomFieldsPage />
            </RequireRole>
          }
        />
        <Route
          path="intake-flow"
          element={
            <RequireRole minimum="admin">
              <IntakeFlowPage />
            </RequireRole>
          }
        />
        <Route
          path="users"
          element={
            <RequireRole minimum="supervisor">
              <UsersPage />
            </RequireRole>
          }
        />
        <Route
          path="teams"
          element={
            <RequireRole minimum="supervisor">
              <TeamsPage />
            </RequireRole>
          }
        />
        <Route
          path="teams/:id"
          element={
            <RequireRole minimum="supervisor">
              <TeamPage />
            </RequireRole>
          }
        />
        <Route
          path="calls"
          element={
            <RequireRole minimum="supervisor">
              <CallsPage />
            </RequireRole>
          }
        />
        <Route
          path="numbers"
          element={
            <RequireRole minimum="supervisor">
              <NumbersPage />
            </RequireRole>
          }
        />
        <Route
          path="campaigns"
          element={
            <RequireRole minimum="supervisor">
              <CampaignsPage />
            </RequireRole>
          }
        />
        <Route
          path="campaigns/:campaignId"
          element={
            <RequireRole minimum="supervisor">
              <CampaignPage />
            </RequireRole>
          }
        />
        <Route
          path="cadences"
          element={
            <RequireRole minimum="supervisor">
              <CadencesPage />
            </RequireRole>
          }
        />
        <Route
          path="cadences/new"
          element={
            <RequireRole minimum="supervisor">
              <CadencePage creating />
            </RequireRole>
          }
        />
        <Route
          path="cadences/:cadenceId"
          element={
            <RequireRole minimum="supervisor">
              <CadencePage />
            </RequireRole>
          }
        />
        <Route
          path="floor"
          element={
            <RequireRole minimum="supervisor">
              <FloorPage />
            </RequireRole>
          }
        />
        <Route
          path="skills"
          element={
            <RequireRole minimum="supervisor">
              <SkillsPage />
            </RequireRole>
          }
        />
        <Route
          path="speed-dials"
          element={
            <RequireRole minimum="supervisor">
              <SpeedDialsPage />
            </RequireRole>
          }
        />
        <Route
          path="blocked-callers"
          element={
            <RequireRole minimum="supervisor">
              <BlockedCallersPage />
            </RequireRole>
          }
        />
        <Route
          path="dispositions"
          element={
            <RequireRole minimum="supervisor">
              <DispositionsPage />
            </RequireRole>
          }
        />
        <Route
          path="audio"
          element={
            <RequireRole minimum="supervisor">
              <AudioPage />
            </RequireRole>
          }
        />
        <Route
          path="voicemail-messages"
          element={
            <RequireRole minimum="supervisor">
              <VoicemailMessagesPage />
            </RequireRole>
          }
        />
        <Route
          path="workflows"
          element={
            <RequireRole minimum="admin">
              <WorkflowsPage />
            </RequireRole>
          }
        />
        <Route
          path="workflows/:workflowId"
          element={
            <RequireRole minimum="admin">
              <WorkflowPage />
            </RequireRole>
          }
        />
        <Route
          path="text-templates"
          element={
            <RequireRole minimum="admin">
              <TextTemplatesPage />
            </RequireRole>
          }
        />
        <Route
          path="text-templates/:slug"
          element={
            <RequireRole minimum="admin">
              <TextTemplatePage />
            </RequireRole>
          }
        />
        <Route
          path="email-templates"
          element={
            <RequireRole minimum="admin">
              <TextTemplatesPage channel="email" />
            </RequireRole>
          }
        />
        <Route
          path="email-templates/:slug"
          element={
            <RequireRole minimum="admin">
              <EmailTemplatePage />
            </RequireRole>
          }
        />
        <Route path="slack" element={<RequireRole minimum="admin"><SlackPage /></RequireRole>} />
        <Route
          path="sms"
          element={
            <RequireRole minimum="admin">
              <SmsSettingsPage />
            </RequireRole>
          }
        />
        <Route
          path="email"
          element={
            <RequireRole minimum="admin">
              <EmailSettingsPage />
            </RequireRole>
          }
        />
        <Route
          path="voices"
          element={
            <RequireRole minimum="admin">
              <VoicesPage />
            </RequireRole>
          }
        />
        <Route
          path="webhooks"
          element={
            <RequireRole minimum="admin">
              <WebhooksPage />
            </RequireRole>
          }
        />
        <Route
          path="webhooks/:endpointId"
          element={
            <RequireRole minimum="admin">
              <WebhookPage />
            </RequireRole>
          }
        />
        <Route
          path="triggers"
          element={
            <RequireRole minimum="admin">
              <TriggersPage />
            </RequireRole>
          }
        />
        <Route
          path="triggers/:triggerId"
          element={
            <RequireRole minimum="admin">
              <TriggerPage />
            </RequireRole>
          }
        />
        <Route
          path="endpoints"
          element={
            <RequireRole minimum="supervisor">
              <EndpointsPage />
            </RequireRole>
          }
        />
        <Route
          path="reports"
          element={
            <RequireRole minimum="supervisor">
              <ReportsRedirect />
            </RequireRole>
          }
        />
        <Route
          path="reports/lines"
          element={
            <RequireRole minimum="supervisor">
              <LineReportPage />
            </RequireRole>
          }
        />
        <Route
          path="reports/campaigns"
          element={
            <RequireRole minimum="supervisor">
              <CampaignReportPage />
            </RequireRole>
          }
        />
        <Route
          path="reports/reps"
          element={
            <RequireRole minimum="supervisor">
              <RepReportPage />
            </RequireRole>
          }
        />
        <Route
          path="domains"
          element={
            <RequireRole minimum="supervisor">
              <DomainsPage />
            </RequireRole>
          }
        />
        <Route
          path="gateways"
          element={
            <RequireRole minimum="admin">
              <GatewaysPage />
            </RequireRole>
          }
        />
        <Route
          path="inventory"
          element={
            <RequireRole minimum="superadmin">
              <InventoryPage />
            </RequireRole>
          }
        />
        <Route
          path="tenants"
          element={
            <RequireRole minimum="superadmin">
              <TenantsPage />
            </RequireRole>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Route>,
  ),
);

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) {
    // Remember where they were headed so the login redirect lands there rather than the root.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <>{children}</>;
}

function RequireRole({ minimum, children }: { minimum: Role; children: React.ReactNode }) {
  const { user } = useAuth();
  if (!atLeast(user?.role, minimum)) {
    return (
      <EmptyState
        className="min-h-[60vh]"
        title="You do not have access to this page"
        description="If you think you should, ask an admin to check your role."
        action={
          <Button variant="outline" asChild>
            <Link to={landingPath(user?.role)}>Back to the console</Link>
          </Button>
        }
      />
    );
  }
  return <>{children}</>;
}

/** Reps land on the phone; everyone else on the list they spend their day in. */
function Landing() {
  const { user } = useAuth();
  return <Navigate to={landingPath(user?.role)} replace />;
}

function NotFound() {
  const { user } = useAuth();
  return (
    <EmptyState
      className="min-h-[60vh]"
      title="Page not found"
      description="That address does not match anything in the console."
      action={
        <Button variant="outline" asChild>
          {/* Link, not <a href>: a full page load here would drop the in-memory access token
              and force a silent refresh for no reason. */}
          <Link to={landingPath(user?.role)}>Back to the console</Link>
        </Button>
      }
    />
  );
}
